import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { RootState } from "typedefs";
import { FamilyMember } from "app/routes/Account/FamilyEdit/types";

import { BookingCandidate } from "./ParticipantPickerModal";
import { clearFamilyRosterCache, getFamilyRoster } from "./familyRosterCache";

/**
 * Loads the caller's managed family members and builds the booking-candidate
 * list (self first, then each managed member) for the "Who is this session for?"
 * picker (CN-1323). Shared by every booking entry point so the candidate list
 * stays consistent. An athlete with no managed members gets `hasFamily: false`
 * and the picker is skipped.
 */
export function useBookingParticipants(): {
  familyMembers: FamilyMember[];
  candidates: BookingCandidate[];
  hasFamily: boolean;
  refetch: () => Promise<void>;
} {
  const { t } = useTranslation();
  const selfUser = useSelector((state: RootState) => state.user?.self);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // CFTB-3301: read through the shared roster cache so a grid of offering
  // cards (each calling this hook) fires a single `family_memberships` GET.
  const load = useCallback(async () => {
    try {
      const list = await getFamilyRoster();
      if (!mounted.current) {
        return;
      }
      setFamilyMembers(list);
    } catch {
      // No roster available (e.g. not entitled) — stay self-only.
    }
  }, []);

  // Exposed so a member added on the spot (CFTB-3277) refreshes the candidates.
  // Invalidate the shared cache first so every consumer picks up the new member.
  const refetch = useCallback(async () => {
    clearFamilyRosterCache();
    await load();
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  const relationshipLabel = (role: FamilyMember["role"]): string => {
    switch (role) {
      case "child":
        return t("Child");
      case "spouse":
        return t("Spouse");
      default:
        return t("Family member");
    }
  };

  const candidates: BookingCandidate[] = [
    {
      id: null,
      name:
        `${selfUser?.firstName ?? ""} ${selfUser?.lastName ?? ""}`.trim() ||
        t("Myself"),
      avatar: (selfUser?.avatar as string) ?? null,
      subLabel: "",
    },
    ...familyMembers.map((member) => ({
      id: member.userId,
      name: `${member.firstName} ${member.lastName}`.trim(),
      avatar: member.avatar,
      subLabel: `${relationshipLabel(member.role)} · ${t("Age {{age}}", {
        age: member.age,
      })}`,
    })),
  ];

  return {
    familyMembers,
    candidates,
    hasFamily: familyMembers.length > 0,
    refetch,
  };
}
